import { UserAvatarBadge } from 'app/components/user-avatar-badge';
import { userInfoAtom } from 'app/atoms';
import { PartagePlusBadge } from 'app/components/partage-plus-badge';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { useHasPartagePlusSubscription } from 'app/hooks/use-has-partage-plus-subscription';
import { useOptimisticAtomState } from 'app/hooks/use-optimistic-state';
import { localeAwareFormat } from 'app/lib/date';
import { useMyProfile } from 'app/network/profile';
import { useAtomValue } from 'jotai/index';
import React from 'react';
import { optimisticUserProfilePictureAtom } from 'app/atoms/ui';

import { Placeholder, PlaceholderLine, PlaceholderMedia, ShineOverlay } from 'app/overrides/rn-placeholder';
import { type ViewProps } from 'react-native';
import { useAmbassador } from 'app/hooks/use-ambassador';
import { createTheme as createDSTheme } from 'app/shopify';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
const theme = createDSTheme();

interface ProfileShortInfoProps {
  alignment?: 'center' | 'flex-start' | 'flex-end';
  thumbSize?: number;
  onlyThumb?: boolean;
  showDetail?: boolean;
}

function ProfileStatus({ isAmbassador, showSuperPartageur }: { isAmbassador: boolean; showSuperPartageur: boolean }) {
  const { t } = useScopedTranslation();
  if (!showSuperPartageur && !isAmbassador) return null;

  return (
    <>
      {showSuperPartageur ? (
        <Text fontWeight="700" marginTop="spacing.general.x1" numberOfLines={1} variant="bodyBase" width="100%">
          {`${t('superPartageur', { ns: 'common' })}${isAmbassador ? ' | ' : ''}`}
        </Text>
      ) : null}
      {isAmbassador ? (
        <Row alignItems="center" gap="spacing.general.x1">
          <Text color="text.brand" fontWeight="700" numberOfLines={1} variant="bodyBase">
            {t('ambassadorLabel', { ns: 'neighbour-profile' })}
          </Text>
        </Row>
      ) : null}
    </>
  );
}
function ProfileShortInfo({
  alignment = 'center',
  thumbSize,
  onlyThumb = false,
  showDetail = true,
}: ProfileShortInfoProps) {
  const userInfo = useAtomValue(userInfoAtom);
  const { t } = useScopedTranslation();
  const { data: profile } = useMyProfile();
  const dWidth = useDeviceWidth();
  const _thumbSize = thumbSize ?? Math.min(100, dWidth / 4);

  const profilePicture = useOptimisticAtomState(optimisticUserProfilePictureAtom, profile?.picture)[0];

  const hasPaidSubscription = useHasPartagePlusSubscription();
  const { isAmbassador } = useAmbassador();
  const isSuper = userInfo?.isSuper || profile?.isSuper || false;
  const showSuperPartageur = isSuper && !isWhiteLabel(WhiteLabel.HauteGaspesie);

  if (!profile) {
    return <ContentSkeleton />;
  }

  if (onlyThumb) {
    return <Thumbnail height={_thumbSize} source={{ uri: profilePicture }} />;
  }

  return (
    <Row minWidth={0} width="100%">
      <Thumbnail
        badge={
          <UserAvatarBadge
            userId={userInfo?.id}
            avatarSize={thumbSize ?? 100}
            size={55}
            isAmbassador={isAmbassador}
            isSuper={showSuperPartageur}
          />
        }
        height={thumbSize}
        source={{ uri: profilePicture }}
      />
      <Column
        flex={1}
        justifyContent={!showDetail ? 'center' : alignment}
        marginStart="spacing.general.x4"
        style={{ minWidth: 0 }}
      >
        <Row
          alignItems="center"
          flexWrap="wrap"
          minWidth={0}
          style={{ columnGap: theme.spacing['spacing.general.x2'], rowGap: theme.spacing['spacing.general.x1'] }}
          width="100%"
        >
          <Text fontWeight="700" numberOfLines={2} style={{ maxWidth: '100%' }} variant="bodyLarge">
            {profile.firstName} {profile.lastName}
          </Text>
          {hasPaidSubscription === undefined ? null : (
            <Column flexShrink={0}>
              <PartagePlusBadge plan={hasPaidSubscription ? 'premium' : 'free'} />
            </Column>
          )}
        </Row>

        {profile.additionalName ? (
          <Text marginTop="spacing.general.x1" numberOfLines={1}>
            {profile.additionalName}
          </Text>
        ) : null}

        <ProfileStatus isAmbassador={isAmbassador} showSuperPartageur={showSuperPartageur} />

        {showDetail ? (
          <Text marginTop="spacing.general.x1" variant="bodyBase">
            {t('memberSince', {
              date: localeAwareFormat(new Date(profile.createdAt), 'MMMM yyyy'),
              ns: 'neighbour-profile',
            })}
          </Text>
        ) : null}
      </Column>
    </Row>
  );
}

export { ProfileShortInfo };

function ContentSkeleton() {
  const leftElement = (props: ViewProps) => (
    <PlaceholderMedia
      style={[
        {
          height: 60,
          width: 60,
          borderRadius: theme.borderRadii['radius.md'],
          marginEnd: theme.spacing['spacing.general.x4'],
        },
        props.style,
      ]}
    />
  );
  return (
    <Placeholder Animation={ShineOverlay} Left={leftElement}>
      <PlaceholderLine style={{ height: 10, width: 200, borderRadius: theme.borderRadii['radius.md'] }} />
      <PlaceholderLine
        style={{
          height: 7.5,
          width: 35,
          borderRadius: theme.borderRadii['radius.md'],
          marginTop: theme.spacing['spacing.general.x1'],
        }}
      />

      <PlaceholderLine style={{ width: 125, height: 7.5, borderRadius: theme.borderRadii['radius.md'] }} />
    </Placeholder>
  );
}
